import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    Output,
    ViewChild,
    Renderer2,
    OnInit,
    OnDestroy,
} from '@angular/core';
import { ScheduleService } from '@common/services/schedule.service';
import { Subscription } from 'rxjs';
import { BaseComponent } from '@components/base-component';
import { StoreDataService } from '@common/services/store-data.service';
import { DialogService } from '@common/services/dialog.service';
import { NgbDatepickerConfig, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { Profile } from '@common/models/profile.model';
import { forkJoin } from 'rxjs';
import { BUTTON_BACK, BUTTON_NEXT } from '@constants/pages-config.constants';
import { OrderParameter } from '@common/models/order-type.model';
import { CHECK_PROFILE_STATE_INT_VALUE } from '@constants/common.constants';

@Component({
    selector: 'app-schedule',
    templateUrl: './schedule.component.html',
    styleUrls: ['./schedule.component.scss'],
    standalone: false,
})
export class ScheduleComponent extends BaseComponent implements OnInit, OnDestroy {
    override progressBarStatus = {
        progress: 3,
        completedStep: 2,
    };
    override navigationSubscription!: Subscription;
    override navigationRoutes = {
        nextRoute: this.pagesUrls[this.pages.SUMMARY],
        backRoute: this.pagesUrls[this.pages.QUOTE],
    };
    override isNextButtonDisabled = false;
    override isNextButtonActive = true;
    nextButtonText = 'Continue';

    profileSubscription!: Subscription;

    displayMonths = 2;
    navigation = 'none';
    outsideDays = 'collapsed';
    firstDayOfWeek = 7;

    todayDate!: Date;
    minDate!: NgbDateStruct;
    maxDate!: NgbDateStruct;
    endOfNextMonth!: Date;
    bookedDates: any[] = [];
    holidays: any[] = [];
    selectedDate: NgbDateStruct | null = null;
    disabledDates: any[] = [];
    pastDates: any[] = [];
    previousSelectedDate: NgbDateStruct | null = null;
    noAvailableDates: boolean = false;
    locationPhone: string | null = null;
    profileState!: Profile;
    checkProfileStateInterval!: ReturnType<typeof setInterval>;
    isContactDialogOpen = false;
    contactDialogData!: {
        phoneNumber: string;
        privacyPolicyUrl: string;
        termsOfUseUrl: string;
        country: string;
    };
    dialogSubscription!: Subscription;
    contactDialogSubscription!: Subscription;
    dialogContactService!: DialogService;
    serverError = false;

    currentKeyDownHandler!: (event: KeyboardEvent) => void;

    @Input() data: any = {}; // TODO remove?
    @Output() valueChanged = new EventEmitter<number>();
    @ViewChild('dp', { read: ElementRef }) dp!: ElementRef;

    constructor(
        private scheduleService: ScheduleService,
        private storeDataService: StoreDataService,
        private cd: ChangeDetectorRef,
        private renderer: Renderer2,
        private datePickerConfig: NgbDatepickerConfig
    ) {
        super();
        this.datePickerConfig.markDisabled = (date: NgbDateStruct) => this.isDisabled(date);

        this.checkProfileStateInterval = setInterval(() => {
            this.storeDataService.updateProfileData(this.profileState);
        }, CHECK_PROFILE_STATE_INT_VALUE);

        this.dialogContactService = new DialogService();
        this.dialogContactService.dialogState$.subscribe((state) => {
            this.isContactDialogOpen = state.isOpen;
            this.contactDialogData = state.data;
        });
    }

    override ngOnInit() {
        super.ngOnInit();
        super.setBrowserBackRoute(this.navigationRoutes.backRoute);
        this.navigationService.setNextButtonActive(this.isNextButtonActive);
        const nextButtonSubscription = this.navigationService
            .getNavigationButtonClicked()
            .subscribe((res) => {
                if (this.isNavigationButtonClicked(res.button) && !res.allowNavigation) {
                    this.storeCurrentData(res.button);
                }
            });

        this.profileSubscription = this.storeDataService
            .profileObserver()
            .subscribe((profile: Profile) => {
                if (profile.profileId && !profile.skipUpdate) this.setupDefaultOptions(profile);
                delete profile.skipUpdate;
            });
        this.addSubscription(this.profileSubscription);
        this.addSubscription(nextButtonSubscription);
    }

    setupDefaultOptions(profile: Profile) {
        this.profileState = JSON.parse(JSON.stringify(profile));
        const today = new Date();

        this.minDate = { year: today.getFullYear(), month: today.getMonth() + 1, day: 1 };
        const nextMonth = new Date(today.getFullYear(), today.getMonth() + 2, 0);
        this.maxDate = {
            year: nextMonth.getFullYear(),
            month: nextMonth.getMonth() + 1,
            day: nextMonth.getDate(),
        };

        today.setHours(0, 0, 0, 0);
        this.todayDate = today;

        this.endOfNextMonth = new Date(today);
        this.endOfNextMonth.setMonth(this.endOfNextMonth.getMonth() + 2);
        this.endOfNextMonth.setDate(0);
        this.endOfNextMonth.setHours(23, 59, 59, 999);

        if (profile.locationPhone) {
            this.locationPhone = this.phoneFormatter(profile.locationPhone);
        }
        if (profile.selectedStartDate) {
            this.selectedDate = this.parseToNgbDateStruct(profile.selectedStartDate as string);
        }
        if (profile.locationCode && profile.firstCleanPrice) {
            this.getDates(
                profile.locationCode,
                profile.firstCleanPrice
            );
        }
    }

    parseToNgbDateStruct(dateString: string): NgbDateStruct | null {
        const [year, month, day] = dateString.split('-').map(Number);

        if (!year || !month || !day) {
            console.error('Invalid date format');
            return null;
        }

        return { year, month, day };
    }

    updateDisabledParentElements(): void {
        if (!this.dp) {
            return;
        }
        const gridCells = this.dp.nativeElement.querySelectorAll('.ngb-dp-day');

        gridCells.forEach((cell: HTMLElement) => {
            const child = cell.querySelector('.custom-day');
            if (
                child?.classList.contains('booked') ||
                child?.classList.contains('holiday') ||
                child?.classList.contains('past')
            ) {
                this.renderer.setStyle(cell, 'pointer-events', 'none');
                this.renderer.addClass(cell, 'disabled');
            }
        });
    }

    filterDatesInRange(dates: any[], startDate: Date, endDate: Date) {
        return dates.filter((date) => {
            const currentDate = new Date(date + 'T00:00:00');
            return currentDate >= startDate && currentDate <= endDate;
        });
    }

    getDates(locationCode: number, price: string) {
        forkJoin({
            daysOffRes: this.scheduleService.getDaysOff(locationCode),
            bookedRes: this.scheduleService.getBookedDates(locationCode, price),
        }).subscribe({
            next: ({ daysOffRes, bookedRes }) => {
                this.holidays = this.filterDatesInRange(
                    daysOffRes.holidays,
                    this.todayDate,
                    this.endOfNextMonth
                );
                this.bookedDates = this.filterDatesInRange(
                    bookedRes.bookedDates,
                    this.todayDate,
                    this.endOfNextMonth
                );

                this.processDates();
                if (!this.selectedDate || this.isDisabled(this.selectedDate)) {
                    this.selectFirstAvailableDate();
                }
                this.cd.markForCheck();
                setTimeout(() => {
                    this.updateDisabledParentElements();
                }, 0);
            },
            error: (err) => {
                console.error('An error occurred during date retrieval:', err);
                this.noAvailableDates = true;
                this.serverError = true;
            },
        });
    }

    onModelChange(event: any) {
        if (event) {
            this.profileState.selectedStartDate = this.convertToDate(event);
            this.googleAnalyticsService.event(
                'default_date_selected',
                'schedule',
                this.profileState.selectedStartDate?.toString()
            );
        }
    }

    processDates() {
        const startDate = new Date(this.todayDate.getFullYear(), this.todayDate.getMonth(), 1);
        let currentDate = new Date(startDate);
        this.pastDates = [];
        while (currentDate < this.todayDate) {
            this.pastDates.push(this.formatDate(currentDate));
            currentDate.setDate(currentDate.getDate() + 1);
        }
        this.disabledDates = [
            ...this.holidays,
            ...this.bookedDates,
            ...this.pastDates,
        ];
    }

    selectFirstAvailableDate() {
        let currentDate = new Date(this.todayDate.getTime());
        while (currentDate <= this.endOfNextMonth) {
            const ngbDate: NgbDateStruct = {
                year: currentDate.getFullYear(),
                month: currentDate.getMonth() + 1,
                day: currentDate.getDate(),
            };
            if (!this.isDisabled(ngbDate)) {
                this.selectedDate = ngbDate;
                this.profileState.selectedStartDate = this.convertToDate(ngbDate);
                this.googleAnalyticsService.event(
                    'default_date_selected',
                    'schedule',
                    this.profileState.selectedStartDate?.toString()
                );
                return;
            }
            currentDate.setDate(currentDate.getDate() + 1);
        }
        this.selectedDate = null;
        this.profileState.selectedStartDate = null;
        this.noAvailableDates = true;
        this.googleAnalyticsService.event(
            'no_available_dates_shown',
            'schedule',
            'No available dates'
        );
    }

    formatDate(date: Date): string {
        const year = date.getFullYear();
        const month = ('0' + (date.getMonth() + 1)).slice(-2);
        const day = ('0' + date.getDate()).slice(-2);
        return `${year}-${month}-${day}`;
    }

    formatDateStruct(date: NgbDateStruct): string {
        const year = date.year;
        const month = ('0' + date.month).slice(-2);
        const day = ('0' + date.day).slice(-2);
        return `${year}-${month}-${day}`;
    }

    isDateBooked(date: NgbDateStruct): boolean {
        return this.bookedDates.includes(this.formatDateStruct(date));
    }

    isHoliday(date: NgbDateStruct): boolean {
        return this.holidays.includes(this.formatDateStruct(date));
    }

    isPast(date: NgbDateStruct): boolean {
        return this.pastDates.includes(this.formatDateStruct(date));
    }

    isDisabled(date: NgbDateStruct): boolean {
        return this.disabledDates.includes(this.formatDateStruct(date));
    }

    onDateSelect(date: NgbDateStruct, event: Event, disabled: boolean): void {
        if (disabled) {
            event.stopPropagation();
            event.preventDefault();
            this.previousSelectedDate = date;
            return;
        }
        this.selectedDate = date;
        this.previousSelectedDate = date;
        this.profileState.selectedStartDate = this.convertToDate(date);
    }

    convertToDate(ngbDate: NgbDateStruct): string | null {
        if (!ngbDate) return null;

        const year = ngbDate.year;
        const month = String(ngbDate.month).padStart(2, '0');
        const day = String(ngbDate.day).padStart(2, '0');

        return `${year}-${month}-${day}`;
    }

    isDateSelected(date: NgbDateStruct): boolean | null {
        return (
            this.selectedDate &&
            this.selectedDate.year === date.year &&
            this.selectedDate.month === date.month &&
            this.selectedDate.day === date.day
        );
    }
    isEndOfWeek(date: NgbDateStruct): boolean {
        const jsDate = new Date(date.year, date.month - 1, date.day);
        return jsDate.getDay() === 4 || jsDate.getDay() === 5 || jsDate.getDay() === 6;
    }

    phoneFormatter(phone: string): string {
        return `${phone.slice(0, 3)}.${phone.slice(3, 6)}.${phone.slice(6, 10)}`;
    }

    refreshTrapFocusInDialog(id: string) {
        document.removeEventListener('keydown', this.currentKeyDownHandler);
        setTimeout(() => {
            this.trapFocusInDialog(id);
        }, 0);
    }

    trapFocusInDialog(id: string) {
        const dialog = document.getElementById(id);
        const focusableElements = Array.from(
            (dialog as HTMLElement)?.querySelectorAll<HTMLElement>(
                'button:not([disabled]), input, label[tabindex="0"], a'
            )
        );
        const firstFocusableElement = focusableElements?.[0];
        const lastFocusableElement = focusableElements?.[focusableElements.length - 1];
        let handleFocus = function (event: KeyboardEvent) {
            if (event.key === 'Tab') {
                if (event.shiftKey) {
                    if (document.activeElement === firstFocusableElement) {
                        lastFocusableElement.focus();
                        event.preventDefault();
                    }
                } else {
                    if (document.activeElement === lastFocusableElement) {
                        firstFocusableElement.focus();
                        event.preventDefault();
                    }
                }
            }
        };
        this.currentKeyDownHandler = handleFocus;
        document.addEventListener('keydown', this.currentKeyDownHandler);
    }

    openContactDialog(dialogType: 'price' | 'date'): void {
        const data = {
            phoneNumber: this.storeDataService.profileData.phone,
            dialogType,
            privacyPolicyUrl: this.storeDataService.profileData.privacyPolicyUrl || '',
            termsOfUseUrl: this.storeDataService.profileData.termsOfUseUrl || '',
            country: this.storeDataService.profileData.country || 'US',
        };
        setTimeout(() => {
            const dialogButtonFocused = document.getElementById('dialogButtonFocused');
            dialogButtonFocused?.focus();
            this.trapFocusInDialog('contactDialog');
        }, 0);
        this.contactDialogSubscription = this.dialogContactService
            .openDialog(data)
            .subscribe((result) => {
                if (result.result?.contactPreference && result.result?.phone) {
                    if (this.checkProfileStateInterval) {
                        clearInterval(this.checkProfileStateInterval);
                    }
                    this.profileState.contactPreference = [];
                    this.profileState.contactPreference.push(result.result.contactPreference);
                    this.profileState.phone = result.result.phone;
                    this.navigationRoutes.nextRoute = this.pagesUrls[this.pages.MANAGER_REQUEST];
                    if (dialogType === 'price') {
                        // TODO replace this code to reusable service for contact dialog
                        this.googleAnalyticsService.event(
                            'contact_me_price_btn_clicked',
                            'contact_me_price',
                            'Contact Me'
                        );
                    } else {
                        this.googleAnalyticsService.event(
                            'contact_me_date_btn_clicked',
                            'contact_me_date',
                            'Contact Me'
                        );
                    }
                    this.storeDataService
                        .storeCurrentData(
                            {
                                ...this.profileState,
                                url: this.pagesUrls[this.pages.MANAGER_REQUEST],
                            },
                            false,
                            true
                        )
                        .subscribe(() => {
                            let param: OrderParameter = 'CustomizeDate';
                            if (dialogType === 'price') {
                                param = 'CustomizePrice';
                            }
                            this.storeDataService.sendOrder(param).subscribe(() => {
                                this.navigationService.setNavigationButtonClicked({
                                    button: BUTTON_NEXT,
                                    allowNavigation: true,
                                });
                            });
                        });
                }
            });
    }

    onContactDialogClosed(event: Event): void {
        this.dialogContactService.closeDialog(event);
        document.removeEventListener('keydown', this.currentKeyDownHandler);
        this.googleAnalyticsService.event(
            'contact_me_popup_closed',
            'contact_me',
            'Close Contact me popup'
        );
        document.getElementById('contactMeBtn')?.focus();
        if (this.contactDialogSubscription) {
            this.contactDialogSubscription.unsubscribe();
        }
    }

    storeCurrentData(button: string) {
        if (this.checkProfileStateInterval) {
            clearInterval(this.checkProfileStateInterval);
        }
        const isBackButton = button === BUTTON_BACK;
        let url = this.navigationRoutes.nextRoute;
        if (isBackButton) {
            url = this.navigationRoutes.backRoute;
            this.profileState.selectedStartDate = null;
        } else {
            this.googleAnalyticsService.event(
                'date_selected',
                'schedule',
                this.profileState.selectedStartDate?.toString()
            );
        }

        this.storeDataService
            .storeCurrentData(
                {
                    ...this.profileState,
                    url: url,
                },
                false,
                true
            )
            .subscribe(() => {
                this.navigationService.setNavigationButtonClicked({
                    button: button,
                    allowNavigation: true,
                });
            });
    }

    override ngOnDestroy(): void {
        if (this.dialogSubscription) {
            this.dialogSubscription.unsubscribe();
        }
        if (this.checkProfileStateInterval) {
            clearInterval(this.checkProfileStateInterval);
        }
        super.ngOnDestroy();
    }
}
